<script setup lang="ts">
import {
  AddMedicalSystemOptionsDto,
  MedicalSystemSubCategory,
  RtSystemCategory,
} from '@/api/generator';
import { api } from '@/api';
import { ref, computed, watchEffect, watch } from 'vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import type { SelectItemTypeObject } from '@/components/forms/types';
import { getTranslatedOptions } from '@/utils/helper';
import { fromPairs } from 'lodash';
import { medicalSystemTypeSubCategory, sectionTasks } from '@/components/icons/icons.ts';
import { useTranslation } from 'i18next-vue';
import { TaskTypeSelection } from '../types';

type Section = keyof TaskTypeSelection;

type AddMedicalSystemSelectOptions = Record<Section, SelectItemTypeObject<string>[]>;

const { t } = useTranslation();

const modalOpen = defineModel<boolean>({ type: Boolean, default: false });

const emits = defineEmits(['submit']);

// selection model
const selections = ref<TaskTypeSelection>({});

// loading indicator
const isLoadingOptions = ref(false);

const loadedOptions = ref<AddMedicalSystemOptionsDto>();

const canSubmit = computed(() => {
  const allSelected =
    selections.value.category && selections.value.subCategory && selections.value.taskType;
  return allSelected && !isLoadingOptions.value;
});

watch(modalOpen, (isOpen) => {
  if (!isOpen && Object.keys(selections.value).length) {
    selections.value = {};
    loadedOptions.value = undefined;
  }
});

const submit = () => {
  emits('submit', selections.value);
};

/**
 * updateSelection
 * @param targetSection
 */
function updateSelection(targetSection: string) {
  // Remove all preceding selections
  let selectionNeedsUpdate = false;

  const sectionOrder = ['category', 'subCategory', 'taskType'];
  const newSelections = sectionOrder.reduce((prevSelection, section) => {
    if (selectionNeedsUpdate) {
      return { ...prevSelection, [section]: undefined };
    } else {
      if (section === targetSection) {
        selectionNeedsUpdate = true;
      }
      return prevSelection;
    }
  }, selections.value);

  selections.value = newSelections;
}

/**
 * updateOptions
 * @param selected
 */
async function updateOptions(selected: TaskTypeSelection = {}) {
  isLoadingOptions.value = true;
  const { result } = await api.medicalSystemsApi.getNewRTSystemOptions(selected);
  if (result) {
    const relevantResult = {
      ...result.category,
      ...result.subCategory,
    };
    loadedOptions.value = result;

    // when receiving options, that have only one possible option, this options should be
    // set immediately if no value has been set before
    let needsReload = false;
    const newSelections = fromPairs(
      Object.entries(selections.value).map(([key, value]) => {
        const targetResult = relevantResult[key as any];
        if (!value && targetResult?.length === 1) {
          needsReload = true;
          return [key, targetResult[0]];
        } else {
          return [key, value];
        }
      }),
    );
    if (needsReload) {
      selections.value = newSelections;
    }
  }
  isLoadingOptions.value = false;
}

/**
 * getTransformedOptions
 * @param keys
 * @param context
 * @param otherEnums
 */
function getTransformedOptions(
  keys: string[] | undefined | null,
  context: string,
  otherEnums: string[],
) {
  if (!keys) {
    return [];
  }

  const translatedOptions: SelectItemTypeObject<string>[] = getTranslatedOptions(
    keys,
    context,
  ).sort((a, b) => a.title.localeCompare(b.title));

  // other-Enums should be moved to the end with a divider
  let filteredOtherOption: SelectItemTypeObject<string> | undefined;

  const transformedOptions: SelectItemTypeObject<string>[] = translatedOptions.filter(
    (translatedOption) => {
      if (otherEnums.includes(translatedOption.value)) {
        filteredOtherOption = translatedOption;
        return false;
      } else {
        return true;
      }
    },
  );
  if (filteredOtherOption) {
    transformedOptions.push({
      ...filteredOtherOption,
      props: { class: 'MedicalSystemAddDialog_divider' },
    });
  }
  return transformedOptions;
}
/**
 * options
 */
const options = computed<AddMedicalSystemSelectOptions>(() => {
  return {
    category: getTransformedOptions(loadedOptions.value?.category, 'RtSystemCategoryEnum', [
      RtSystemCategory.OtherMedicalSystem,
    ]),
    subCategory: getTransformedOptions(
      loadedOptions.value?.subCategory,
      'MedicalSystemSubCategoryEnum',
      [
        MedicalSystemSubCategory.OtherImagingMachine,
        MedicalSystemSubCategory.OtherMedicalSystem,
        MedicalSystemSubCategory.OtherSoftware,
        MedicalSystemSubCategory.OtherTreatmentMachine,
      ],
    ),
    taskType:
      selections.value.category === RtSystemCategory.TreatmentMachine &&
      selections.value.subCategory === 'MVTherapy'
        ? [
            {
              title: t('TaskTypeDescriptionEnum.ManualTask'),
              value: 'ManualTask',
            },
            {
              title: t('TaskTypeDescriptionEnum.BeamConstancyCheck'),
              value: 'BeamConstancyCheck',
            },
            {
              title: t('TaskTypeDescriptionEnum.BeamAnalysis'),
              value: 'BeamAnalysis',
            },
          ]
        : [
            {
              title: t('TaskTypeDescriptionEnum.ManualTask'),
              value: 'ManualTask',
            },
          ],
  };
});

watchEffect(() => updateOptions(selections.value));
</script>

<template>
  <Dialog v-model="modalOpen" size="medium">
    <template #title>{{ $t('ChooseTask') }} </template>

    <template #text>
      <Autocomplete
        v-model="selections.category"
        :label="$t('Category_one')"
        :items="options.category"
        :disabled="options.category.length === 1"
        :placeholder="$t('Select')"
        @update:modelValue="updateSelection('category')"
      />
      <Autocomplete
        v-if="selections.category"
        v-model="selections.subCategory"
        :prependInnerIcon="medicalSystemTypeSubCategory"
        class="gutterTop"
        :label="$t('Type_one')"
        :items="options.subCategory"
        :placeholder="$t('Select')"
        @update:modelValue="updateSelection('subCategory')"
      />
      <Autocomplete
        v-if="selections.subCategory"
        v-model="selections.taskType"
        :prependInnerIcon="sectionTasks"
        class="gutterTop"
        :label="$t('TaskType_one')"
        :items="options.taskType"
        :active="true"
        :placeholder="$t('Select')"
        @update:modelValue="updateSelection('taskType')"
      >
      </Autocomplete>
    </template>

    <template #actions>
      <SecondaryVBtn :disabled="!canSubmit" @click="submit">
        {{ $t('Apply') }}
      </SecondaryVBtn>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
:global(.MedicalSystemAddDialog_divider) {
  border-top: 1px solid rgb(var(--v-theme-primary-lighten-4));
}
</style>
